import React, { useState, useEffect } from 'react'
import { ContentWrapper } from './Style'
import AssessmentContainer from './Partials/AssessmentContainer'
import CommunicationInstructions from './Partials/Communicationassmt/instruction'
import BehaviourInstructions from './Partials/Behaviourassmt/instruction'
import AptitudeInstructions from './Partials/aptitudeassmt/instruction'
import RoleBasedInstructions from './Partials/RoleBasedassmt/instruction'
import CustomInstructions from './Partials/customassmt/instruction'
import HinglishInstructions from './Partials/Hinglishassmt/instruction'
import AIInterview from './Partials/AIInterview'
import { useSelector } from 'react-redux'

const Assessment = ({
  questions,
  totalQuestions,
  loading,
  error,
  responses,
  fetchAssessmentQuestions,
  saveQuestionResponse,
  activeAssessments,
  activeAssessmentsLoading,
  activeAssessmentsError,
  activeAssessmentsFilters,
  completedAssessments,
  completedAssessmentsLoading,
  completedAssessmentsError,
  completedAssessmentsFilters,
  fetchActiveAssessments,
  fetchCompletedAssessments,
  setActiveAssessmentFilters,
  setCompletedAssessmentFilters,
  setCurrentAssessmentId,
  fetchCommunicationInstructions,
  dropoutAssessments,
  dropoutAssessmentsLoading,
  dropoutAssessmentsError,
  dropoutAssessmentsFilters,
  fetchDropoutAssessments,
  setDropoutAssessmentFilters,
}) => {
  const [assessment, setAssessment] = useState(null)
  const { studentDetails } = useSelector(state => state?.studentData)
  const studentId = studentDetails?.id

  /*
   * Taking an assessment now happens in the v2 candidate journey, from its
   * instruction screen onwards. This dashboard stays where it is — v2 has no
   * screen that lists a student's assessments — so it hands the chosen
   * assignment over and the student comes back here when they are done.
   *
   * Same origin, so v2 reads the signed-in token itself and trades it for a
   * session scoped to this one assignment. A relative path on purpose: an
   * absolute one built from an env var is how a DEV URL ends up baked into a
   * UAT bundle.
   */
  const v2Href =
    studentId && assessment?.assessment_assigned_id
      ? `/candidate-assessment-journey/v2/assessment?assigned=${encodeURIComponent(
          assessment.assessment_assigned_id
        )}&back=${encodeURIComponent(
          `${window.location.pathname}${window.location.search}`
        )}`
      : null

  useEffect(() => {
    if (v2Href) window.location.assign(v2Href)
  }, [v2Href])

  // Handing over: the v1 instruction screens below must not flash first.
  if (v2Href) {
    return (
      <ContentWrapper>
        <div style={{ padding: '48px 0', textAlign: 'center', color: '#64748b' }}>
          Opening your assessment…
        </div>
      </ContentWrapper>
    )
  }

  if (assessment) {
    if (assessment.type === 'Behavior') {
      return (
        <BehaviourInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }
    if (assessment.type === 'Communication') {
      return (
        <CommunicationInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchCommunicationInstructions={fetchCommunicationInstructions}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }
    if (assessment.type === 'Hinglish' || assessment.type?.toLowerCase() === 'hinglish') {
      return (
        <HinglishInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchCommunicationInstructions={fetchCommunicationInstructions}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }
    if (assessment.type === 'Aptitude') {
      return (
        <AptitudeInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }

    if (assessment.type === 'Role-Based' || assessment.type === 'Role_Based' || assessment.type?.toLowerCase().includes('role')) {
      return (
        <RoleBasedInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }

    if (
      assessment.type === 'AI_Interview' ||
      assessment.type === 'AI Interview' ||
      assessment.type?.toLowerCase().replace(/[\s_-]/g, '') === 'aiinterview'
    ) {
      return (
        <AIInterview
          assessment={assessment}
          setAssessment={setAssessment}
        />
      )
    }


    // ✅ Make this check more flexible with case-insensitive and underscore/space variations
    if (
      assessment.type === 'Custom_Assessment' || 
      assessment.type === 'Custom Assessment' ||
      assessment.type === 'custom_assessment' ||
      assessment.type === 'custom assessment' ||
      assessment.type?.toLowerCase().replace(/[\s_]/g, '') === 'customassessment'
    ) {
      console.log('🎯 Routing to Custom Assessment with data:', assessment) // Debug log
      return (
        <CustomInstructions
          assessment={assessment}
          questions={questions}
          totalQuestions={totalQuestions}
          loading={loading}
          error={error}
          responses={responses}
          fetchAssessmentQuestions={fetchAssessmentQuestions}
          saveQuestionResponse={saveQuestionResponse}
          studentId={studentId}
          setAssessment={setAssessment}
        />
      )
    }
    
    // Fallback for unrecognized assessment types
    console.warn('Unrecognized assessment type:', assessment.type)
  }

  return (
    <ContentWrapper>
      <AssessmentContainer
        loading={loading}
        error={error}
        setAssessment={setAssessment}
        activeAssessments={activeAssessments}
        activeAssessmentsLoading={activeAssessmentsLoading}
        activeAssessmentsError={activeAssessmentsError}
        activeAssessmentsFilters={activeAssessmentsFilters}
        completedAssessments={completedAssessments}
        completedAssessmentsLoading={completedAssessmentsLoading}
        completedAssessmentsError={completedAssessmentsError}
        completedAssessmentsFilters={completedAssessmentsFilters}
        fetchActiveAssessments={fetchActiveAssessments}
        fetchCompletedAssessments={fetchCompletedAssessments}
        setActiveAssessmentFilters={setActiveAssessmentFilters}
        setCompletedAssessmentFilters={setCompletedAssessmentFilters}
        studentId={studentId}
        setCurrentAssessmentId={setCurrentAssessmentId}
        dropoutAssessments={dropoutAssessments}
        dropoutAssessmentsLoading={dropoutAssessmentsLoading}
        dropoutAssessmentsError={dropoutAssessmentsError}
        dropoutAssessmentsFilters={dropoutAssessmentsFilters}
        fetchDropoutAssessments={fetchDropoutAssessments}
        setDropoutAssessmentFilters={setDropoutAssessmentFilters}
      />
    </ContentWrapper>
  )
}

export default Assessment
